import { useState, type FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const SERVICES_LIST = [
  'Full Rewire', 'New Installation', 'Lighting', 'Fuse Board Upgrade',
  'EV Charger', 'Fault Finding', 'Commercial', 'Consumer Unit',
  'Smart Home', 'EICR / Condition Report', 'Bathroom Electrics', 'Solar / Battery',
];

const CONTACT_INFO = [
  { icon: 'ri-phone-fill', label: 'Phone', value: '01529 265060', href: 'tel:01529265060' },
  { icon: 'ri-mail-fill', label: 'Email', value: 'info@quantum-electrical.uk', href: 'mailto:info@quantum-electrical.uk' },
  { icon: 'ri-map-pin-2-fill', label: 'Location', value: 'Sleaford, Lincolnshire, NG34', href: '#' },
  { icon: 'ri-time-fill', label: 'Hours', value: 'Mon–Fri 8am–6pm · Sat 9am–4pm', href: '#' },
];

type FormState = 'idle' | 'sending' | 'success' | 'error';

export default function Contact() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));

    setFormState('sending');
    try {
      const res = await fetch('https://readdy.ai/api/form/d72e81kij602egd95g6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setFormState(res.ok ? 'success' : 'error');
      if (res.ok) form.reset();
    } catch {
      setFormState('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#071120] relative overflow-hidden">
      {/* Gold background glow */}
      <div
        className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #C8A96A, transparent)' }}
      />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium mb-3">Get In Touch</p>
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white leading-tight">
            Get Your{' '}
            <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">
              Free Quote
            </span>{' '}
            Today
          </h2>
          <p className="text-white/60 mt-4 max-w-lg mx-auto">
            No obligation, transparent pricing. We&apos;ll get back to you within 2 hours during business hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {CONTACT_INFO.map(({ icon, label, value, href }) => (
              <a
                key={label}
                href={href}
                className="flex items-center gap-4 bg-white/5 border border-white/10 hover:border-[#C8A96A]/40 rounded-2xl p-5 transition-all duration-300 cursor-pointer group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#C8A96A]/15 group-hover:bg-[#C8A96A]/25 rounded-xl transition-colors duration-300 flex-shrink-0">
                  <i className={`${icon} text-[#C8A96A] text-xl`} />
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider">{label}</p>
                  <p className="text-white font-medium text-sm mt-0.5">{value}</p>
                </div>
              </a>
            ))}

            <div className="border-t border-white/10 pt-6">
              <p className="text-white/50 text-xs uppercase tracking-wider mb-4">Certifications</p>
              <div className="flex flex-wrap gap-3">
                {['NAPIT', 'Part P', 'OLEV EV', '18th Edition'].map((cert) => (
                  <span
                    key={cert}
                    className="text-[#C8A96A] border border-[#C8A96A]/30 text-xs px-3 py-1.5 rounded-full"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <form
              id="quote-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-[#0D1E35]/80 border border-white/10 rounded-2xl p-8 flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Full Name *</label>
                  <input
                    name="name"
                    type="text"
                    required
                    placeholder="Your name"
                    className="w-full bg-white/5 border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 placeholder-white/30"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Phone *</label>
                  <input
                    name="phone"
                    type="tel"
                    required
                    placeholder="01529 xxxxxx"
                    className="w-full bg-white/5 border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 placeholder-white/30"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Email</label>
                <input
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  className="w-full bg-white/5 border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 placeholder-white/30"
                />
              </div>

              <div>
                <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Service Required</label>
                <select
                  name="service"
                  required
                  className="w-full bg-[#0D1E35] border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 cursor-pointer"
                >
                  <option value="">Select a service</option>
                  {SERVICES_LIST.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">
                  Message ({charCount}/500)
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  placeholder="Tell us about the work you need..."
                  className="w-full bg-white/5 border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 placeholder-white/30 resize-none"
                />
                {charCount > 450 && (
                  <p className="text-[#E0C78A] text-xs mt-1">{500 - charCount} characters remaining</p>
                )}
              </div>

              {formState === 'success' && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-green-400 text-xl" />
                  <p className="text-green-400 text-sm">Quote request sent! We&apos;ll be in touch very soon.</p>
                </div>
              )}
              {formState === 'error' && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
                  <i className="ri-error-warning-fill text-red-400 text-xl" />
                  <p className="text-red-400 text-sm">Something went wrong. Please call us on 01529 265060.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={formState === 'sending'}
                className="w-full bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#E0C78A] hover:to-[#C8A96A] text-black font-bold py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 cursor-pointer whitespace-nowrap"
              >
                {formState === 'sending' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" />
                    Request Free Quote
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
